import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { RootState } from '../../redux/store';
import useGet from '../../hooks/Common/useGet';
import { useParams } from 'react-router-dom';
import getDetections from '../../web_api/methods/alerts/getDetections';
import AlertCard from './AlertCard';
import LoadingPage from '../../layout/LoadingPage';
import useToolbarSetter from '../../hooks/Common/useToolbarSetter';
import getDetectionVideos from '../../web_api/methods/alerts/getDetectionVideos';
import getAlertsExport from '../../web_api/methods/alerts/getAlertsExport';
import Slideshow, { EmbeddedVideoData } from '../Common/Slider';
import ExpandableComponent from '../Common/ExpandableComponent';
import deleteAlerts from '../../web_api/methods/alerts/deleteAlerts';
import { CommonModal } from '../Common/CommonModal';
interface RouteParams {
  [key: string]: string | undefined;
}

type TimeRangeOption = {
  value: string;
  label: string;
  startHour: number;
  endHour: number;
};

const formatHourLabel = (hour: number) => {
  if (hour === 24) {
    return '24:00';
  }

  return `${`${hour}`.padStart(2, '0')}:00`;
};

const timeRangeOptions: TimeRangeOption[] = Array.from({ length: 24 }, (_, hour) => ({
  value: `${hour}-${hour + 1}`,
  label: `${formatHourLabel(hour)} - ${formatHourLabel(hour + 1)}`,
  startHour: hour,
  endHour: hour + 1,
}));

const buildExportWindow = (fromDate: Date, toDate: Date, timeRange: TimeRangeOption) => {
  const exportFrom = new Date(fromDate);
  exportFrom.setHours(timeRange.startHour, 0, 0, 0);

  const exportTo = new Date(toDate);
  exportTo.setHours(timeRange.endHour, 0, 0, 0);

  return { exportFrom, exportTo };
};

const formatExportDate = (date: Date) =>
  date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

export default function Alerts() {
  const { setToolbar } = useToolbarSetter();
  useEffect(() => {
    setToolbar('alerts', `Alerts - ${detectionTypeName}`);
  });
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const activeLocation = useSelector((state: RootState) => state.activeLocation);
  const selectedUser = useSelector((state: RootState) => state.selectedUser);
  const selectedCamera = useSelector((state: RootState) => state.selectedCamera);
  const dateFilter = useSelector((state: RootState) => state.dateFilter);
  const params = useParams<RouteParams>();
  const detectionType = params.detectionType;
  const detectionTypeName = params.detectionTypeName;
  const [videoUrls, setVideoUrls] = useState<EmbeddedVideoData[]>([]);
  const [videoExists, setVideoExists] = useState(false);
  const [selectedAlerts, setSelectedAlerts] = useState<string[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('');
  const selectedTimeRangeOption = timeRangeOptions.find((timeRange) => timeRange.value === selectedTimeRange);
  const exportWindow =
    selectedTimeRangeOption !== undefined
      ? buildExportWindow(new Date(dateFilter.from), new Date(dateFilter.to), selectedTimeRangeOption)
      : null;

  const { loading, data, loadItems, setData } = useGet([selectedUser.id, activeLocation.id, selectedCamera.id, pageIndex, pageSize], () =>
    getDetections(parseInt(detectionType!), parseInt(activeLocation.id), pageIndex + 1, pageSize, new Date(dateFilter.from), new Date(dateFilter.to))
  );

  const handleNext = () => {
    setPageIndex((prevPageIndex) => prevPageIndex + 1);
  }; 

  const handlePrev = () => {
    setPageIndex((prevPageIndex) => Math.max(prevPageIndex - 1, 0));
  };

  const handleAlertSelect = (alertID: string) => {
    if (!selectedAlerts.includes(alertID)) {
      setSelectedAlerts((prev) => [...prev, alertID]);
      return;
    }

    setSelectedAlerts((prev) => prev.filter((el) => el !== alertID));
  };

  const handleDeleteLocalEntries = (alerts: string[]) => {
    setData((prev) => prev?.filter((obj) => !alerts.includes(obj.detection_gu_id)));
    setSelectedAlerts([]);
  };

  const loadVideos = async () => {
    try {
      setVideoExists(false);
      let videoUrlsArray: EmbeddedVideoData[] = [];
      let response = await getDetectionVideos(detectionTypeName ?? 'none', parseInt(detectionType!));
      response.forEach((item) => {
        let x: EmbeddedVideoData = { src: item, title: detectionTypeName ?? '', description: '' };
        videoUrlsArray.push(x);
      });
      setVideoUrls(videoUrlsArray);
      if (videoUrlsArray.length !== 0) setVideoExists(true);
    } catch (error) {
      setVideoExists(false);
    }
  };
  useEffect(() => {
    loadVideos();
  }, [detectionType]);
  let loadAlerts = loadItems;
  useEffect(() => {
    setPageIndex(0); // reset page index when selectedUser changes
    loadAlerts();
  }, [activeLocation.id, selectedUser.id, dateFilter.from, dateFilter.to, detectionType]);

  const handleExportModal = (active: boolean) => {
    if (isExporting) {
      return;
    }

    setShowExportModal(active);
  };

  const handleDeleteAlertsModal = () => {
    setShowConfirmDeleteModal((prev) => !prev);
  };

  const handleExportAlerts = async () => {
    if (!selectedTimeRangeOption || exportWindow === null) {
      toast.error('Please select a time range.', {
        position: 'top-right',
        theme: 'colored',
      });
      return;
    }

    const locationId = parseInt(activeLocation.id);
    const detectionTypeId = parseInt(detectionType ?? '');

    if (Number.isNaN(locationId) || Number.isNaN(detectionTypeId)) {
      toast.error('Missing export filters. Please select a valid location and alert type.', {
        position: 'top-right',
        theme: 'colored',
      });
      return;
    }

    setIsExporting(true);

    try {
      const { content, fileName } = await getAlertsExport(locationId, exportWindow.exportFrom, exportWindow.exportTo, detectionTypeId);
      const downloadUrl = window.URL.createObjectURL(content);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      setShowExportModal(false);
      setSelectedTimeRange('');

      toast.success('Alerts exported successfully!', {
        position: 'top-right',
        theme: 'colored',
        autoClose: 2000,
      });
    } catch (error: any) {
      let errorMessage = 'Unable to export alerts.';

      if (error?.response?.data instanceof Blob) {
        try {
          const blobMessage = await error.response.data.text();
          if (blobMessage) {
            errorMessage = blobMessage;
          }
        } catch {
          errorMessage = 'Unable to export alerts.';
        }
      } else if (typeof error?.message === 'string' && error.message.length > 0) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        position: 'top-right',
        theme: 'colored',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleAlertCardDelete = (alertID: string) => {
    try {
      deleteAlerts([alertID]).then(() => {
        handleDeleteLocalEntries([alertID]);

        toast.success('Alert deleted successfully!', {
          position: 'top-right',
          theme: 'colored',
          autoClose: 2000,
        });
      });
    } catch (e) {
      console.log(e);
      toast.error(`${e}`, {
        position: 'top-right',
        theme: 'colored',
      });
    }
  };

  // useEffect(() => {
  //     if (videoUrls?.length !== 0)
  //         setVideoExists(true)
  // }, [videoUrls?.length]);
  return (
    <div className='container'>
      {videoExists && (
        <ExpandableComponent className='d-flex flex-column flex-md-column col-12 p-5 pb-0' label={'Examples'}>
          {videoUrls?.length === 1 ? (
            <div className='d-flex flex-center p-5'>
              <video width='800' style={{ borderRadius: '25px' }} controls autoPlay>
                <source src={videoUrls[0].src} type='video/mp4' />
                Your browser does not support the video tag.
              </video>
            </div>
          ) : (
            <>
              <Slideshow data={videoUrls} autoplay={false} />
            </>
          )}
        </ExpandableComponent>
      )}
      <div className='d-flex flex-column flex-md-column col-12 p-5 pb-0'>
        <div className=''>
          <div
            style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
            className='d-flex flex-column flex-md-row p-1 flex-end'
          >
            <div>
              <button className={`btn btn-danger ${selectedAlerts.length > 0 ? '' : 'd-none'}`} onClick={handleDeleteAlertsModal}>
                Delete selection
              </button>
              <button className={`btn btn-primary ms-2`} onClick={() => handleExportModal(true)}>
                Export alerts
              </button>
            </div>
            <div>
              <button className='btn btn-primary' onClick={handlePrev} disabled={pageIndex === 0}>
                <i className='bi bi-chevron-double-left p-0' />
              </button>
              <button className='btn btn-secondary' disabled>
                {pageIndex + 1}
              </button>
              <button className='btn btn-primary' disabled={data === undefined || data.length < 30} onClick={handleNext}>
                <i className='bi bi-chevron-double-right p-0' />
              </button>
            </div>
          </div>
        </div>
        <div className='row'>
          {!loading ? (
            data &&
            data.map((alert) => (
              <>
                <AlertCard key={alert.detection_gu_id} alert={alert} onAlertSelect={handleAlertSelect} onAlertDelete={handleAlertCardDelete} />
              </>
            ))
          ) : (
            <LoadingPage />
          )}
        </div>
      </div>
      {/* <CommonModal
                size='xl'
                title={personItem!.person}
                show={showPicture}
                showModalCallback={() => toggleModal(0)}
                closeButton={true}
                confirmButton={false}
                bodyClass="flex-column w-100"
                closeButtonOptions={{
                    icon: 'bi bi-x',
                    variant: 'secondary',
                    class: "",
                }}
                confirmButtonOptions={{
                    icon: "bi bi-trash3",
                    variant: 'custom',
                    class: "bg-danger text-white",
                }}
            >
                <CanvasComponent

                    imageUrl={`/alerts-media/${tenantKey}/${new Date().getFullYear()}/${new Date().getMonth() + 1}/${personItem?.filename}`}
                    actualHeight={personItem!.picture_height}
                    actualWidth={personItem!.picture_width}
                    bboxes={[{ x1: personItem!.bbox1, y1: personItem!.bbox2, x2: personItem!.bbox3, y2: personItem!.bbox4 }]}
                    canvasHeight={"500px"}
                    canvasWidth={"100%"} />
            </CommonModal > */}

      <CommonModal
        show={showExportModal}
        title='Export Alerts'
        size='lg'
        bodyClass='d-flex flex-col w-100 gap-2 justify-content-start'
        disabled={isExporting}
        closeButton={true}
        confirmButton={true}
        showModalCallback={handleExportModal}
        closeButtonOptions={{
          icon: 'bi bi-x-lg text-black',
          variant: 'secondary',
          class: 'd-flex flex-row justify-content-center align-items-center ',
        }}
        confirmButtonOptions={{
          icon: 'bi bi-arrow-bar-up text-white',
          variant: 'custom',
          class: 'bg-primary text-white d-flex flex-row align-items-center',
          text: isExporting ? 'Exporting...' : 'Export',
          onClick: handleExportAlerts,
        }}
      >
        <div className='d-flex flex-column w-100 gap-3'>
          <label htmlFor='export-time-range' className='form-label'>
            Time range
          </label>
          <select
            id='export-time-range'
            className='form-select'
            disabled={isExporting}
            value={selectedTimeRange}
            onChange={(event) => setSelectedTimeRange(event.target.value)}
          >
            <option value=''>Select a time range</option>
            {timeRangeOptions.map((timeRange) => (
              <option key={timeRange.value} value={timeRange.value}>
                {timeRange.label}
              </option>
            ))}
          </select>
          {exportWindow !== null && (
            <div className='alert alert-light-primary mb-0 py-3'>
              <div className='fw-semibold'>Export window</div>
              <div>{`${formatExportDate(exportWindow.exportFrom)} - ${formatExportDate(exportWindow.exportTo)}`}</div>
            </div>
          )}
          {isExporting && (
            <div className='d-flex flex-row align-items-center justify-content-center gap-3 py-2'>
              <div className='spinner-border text-primary' role='status' aria-hidden='true' />
              <span>Preparing the alerts export archive...</span>
            </div>
          )}
        </div>
      </CommonModal>

      <CommonModal
        show={showConfirmDeleteModal}
        title='Are you sure you want to delete the selected alerts?'
        size='lg'
        bodyClass='d-flex flex-col w-100 gap-2 justify-content-start'
        closeButton={true}
        confirmButton={true}
        showModalCallback={handleDeleteAlertsModal}
        closeButtonOptions={{
          icon: 'bi bi-x-lg text-black',
          variant: 'secondary',
          class: 'd-flex flex-row justify-content-center align-items-center ',
        }}
        confirmButtonOptions={{
          icon: 'bi bi-trash3 text-white',
          variant: 'custom',
          class: 'bg-danger text-white d-flex flex-row align-items-center',
          onClick: () => {
            setShowConfirmDeleteModal(false);
            deleteAlerts(selectedAlerts);
            // setTimeout(() => window.location.reload(), 500);
            handleDeleteLocalEntries(selectedAlerts);
          },
        }}
      >
        <ul>
          {selectedAlerts.map((alert) => (
            <li>{`${alert}`}</li>
          ))}
        </ul>
      </CommonModal>
    </div>
  );
}
